import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { markSpotlightSeen, dismissSpotlight, trySpotlight } from "@/api/featureSpotlights";
import { spotlightTarget } from "@touchworks/shared/platform/spotlightTargets.js";
import { resolveDestination } from "./resolveDestination";
import { showsOnMenu } from "./spotlightSurface";
import { useAnchorElement } from "./useAnchorElement";
import { CANDIDATES_QUERY_KEY, useCandidates } from "./useCandidates";
import { useSpotlightGate } from "./useSpotlightGate";
import { useIsDesktop } from "./useMediaMatch";

/** Sem regra de plano/integração do chamador: todo recurso conhecido vale. */
const allowEveryTarget = () => true;

/**
 * Decide qual novidade aparece, quando, e o que acontece nos dois botões.
 *
 * - Escolhe a primeira da lista da API (já ordenada por prioridade) cujo item de
 *   menu a pessoa de fato enxerga. As puladas nunca viram "visto" (SPOT-08). As que só
 *   aparecem na tela inicial (`surface: "home"`) não entram aqui (ver `useHomeSpotlight`).
 * - Espera quem bloqueia a tela (SPOT-07): comunicado de plataforma, portão de termos, o tour
 *   obrigatório de boas-vindas (etapa ativa no armazenamento da conta) e qualquer overlay
 *   que se declare com `data-blocking-overlay`. No desktop espera também o item de menu aparecer.
 * - Pula a novidade cujo recurso a pessoa não tem (plano, integração): sem "visto" (SPOT-18).
 * - Cede a vez ao ponteiro (`suppressed`): nunca duas superfícies de novidade ao mesmo tempo.
 * - Registra "visto" uma única vez por novidade, no momento em que ela aparece.
 * - Depois de fechar ou experimentar, nenhuma outra aparece até o próximo
 *   carregamento do app (SPOT-02). Falha de rede nunca atrapalha a pessoa: o
 *   cartão some do mesmo jeito, a dispensa é tentada duas vezes e, se ainda falhar, refeita
 *   no próximo carregamento.
 * - Se a novidade da tela sai da lista do servidor (pausada ou encerrada), o cartão some (SPOT-40).
 *
 * @param {{
 *   visibleNavKeys: string[],
 *   resolveAccountPath: (subPath: string) => string,
 *   canSeeTarget?: (target: object) => boolean,
 *   tourClientId?: string|null,
 *   suppressed?: boolean,
 *   enabled?: boolean,
 * }} params
 */
export function useFeatureSpotlight({
  visibleNavKeys,
  resolveAccountPath,
  canSeeTarget = allowEveryTarget,
  tourClientId = null,
  suppressed = false,
  enabled = true,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isDesktop = useIsDesktop();
  const { data } = useCandidates(enabled);
  // Sem nenhuma novidade na lista, não há o que esperar: nenhum observador de DOM.
  const gated = useSpotlightGate({ tourClientId, enabled: enabled && (data?.cards?.length ?? 0) > 0 });

  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const [pinnedId, setPinnedId] = useState(null);
  const seenIds = useRef(new Set());

  const keysSignature = visibleNavKeys.join("|");
  const cards = data?.cards;
  const spotlight = useMemo(() => {
    if (!enabled || resolvedIds.size > 0) return null;
    const keys = keysSignature.split("|");
    const eligible = (cards ?? []).filter((card) => {
      // O destaque só da tela inicial não vira cartão ao lado do menu
      if (!showsOnMenu(card) || !keys.includes(card.anchorNavKey)) return false;
      // Chave de recurso que este cliente não conhece (cliente mais velho que o servidor): o cartão
      // segue, o destino cai na funcionalidade âncora.
      const target = card.targetKey ? spotlightTarget(card.targetKey) : null;
      return !target || canSeeTarget(target);
    });
    // Uma lista nova (a busca refaz a cada 5 min) não troca o cartão que já está na tela.
    const pinned = eligible.find((card) => card.id === pinnedId);
    if (pinned) return pinned;
    // O cartão da tela saiu da lista do servidor (pausado, encerrado, público mudou): some, e
    // nenhuma outra assume o lugar antes do próximo carregamento (SPOT-02, SPOT-40).
    if (pinnedId && !(cards ?? []).some((card) => card.id === pinnedId)) return null;
    return eligible[0] ?? null;
    // `canSeeTarget` é refeito pelo layout quando o plano carrega ou muda; o resultado é o mesmo
    // objeto da lista (a identidade do cartão escolhido não muda), então refazer a cada render é barato.
  }, [enabled, resolvedIds, cards, keysSignature, pinnedId, canSeeTarget]);

  useEffect(() => {
    if (spotlight && spotlight.id !== pinnedId) setPinnedId(spotlight.id);
  }, [spotlight, pinnedId]);

  const anchorEl = useAnchorElement(isDesktop && spotlight ? spotlight.anchorNavKey : null);

  const waiting = gated || suppressed;
  const visible = Boolean(spotlight) && !waiting && (!isDesktop || Boolean(anchorEl));

  useEffect(() => {
    if (!visible || seenIds.current.has(spotlight.id)) return;
    seenIds.current.add(spotlight.id);
    // Idempotente no servidor. Falha de rede não atrapalha a pessoa, só atrasa a métrica.
    markSpotlightSeen(spotlight.id).catch(() => {});
  }, [visible, spotlight]);

  // Clique duplo, ou os dois botões em sequência, só valem uma vez.
  const resolving = useRef(new Set());
  const resolveOnce = useCallback((id, run) => {
    if (resolving.current.has(id)) return;
    resolving.current.add(id);
    setResolvedIds((previous) => new Set(previous).add(id));
    run();
  }, []);

  const dismiss = useCallback(() => {
    if (!spotlight) return;
    resolveOnce(spotlight.id, () => {
      // Esconde já; se a gravação falhar, tenta uma segunda vez e desiste (volta no próximo carregamento).
      const save = () => dismissSpotlight(spotlight.id);
      save().catch(save).catch(() => {});
    });
  }, [spotlight, resolveOnce]);

  const tryIt = useCallback(() => {
    if (!spotlight) return;
    resolveOnce(spotlight.id, () => {
      // O destino sai do próprio cartão e é aplicado dentro do clique: abrir uma aba depois de
      // aguardar a API cai no bloqueador de pop-ups, e a navegação não precisa esperar a gravação.
      const destination = resolveDestination(
        { ...spotlight.ctaTarget, anchorNavKey: spotlight.anchorNavKey, targetKey: spotlight.targetKey },
        resolveAccountPath,
      );
      if (destination?.type === "external") window.open(destination.url, "_blank", "noopener,noreferrer");
      else if (destination) navigate(destination.to);
      // Depois de gravar, relê a lista: se a novidade tem um recurso, o servidor já devolve o
      // ponteiro armado e ele aparece na tela de destino.
      trySpotlight(spotlight.id)
        .then(() => queryClient.invalidateQueries({ queryKey: CANDIDATES_QUERY_KEY }))
        .catch(() => {});
    });
  }, [spotlight, resolveOnce, resolveAccountPath, navigate, queryClient]);

  return { spotlight, visible, anchorEl, isDesktop, dismiss, tryIt };
}
